import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ContrastIcon from '@mui/icons-material/Contrast';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButtonWhiteBg from '../../../common/IconButtonWhiteBg';
import { Badge } from '@mui/material';
import { AppContext } from '../../../../App';
import { useAtom, useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import {
  markValidationByDocumentAtom,
  markValidationsEnabledAtom,
  markShowValidationsAtom,
  showMarkValidationErrorsDialogAtom,
} from '../../../../atoms/mark-validation-atoms';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SvgIconValidate from '../../../../images/icons/Glynt_icon_Revalidate.svg?react';

const iconStyles = {
  mr: 1,
  fontSize: 16,
};

const statusIconStyles = {
  ml: 'auto',
  fontSize: 16,
};

const textStyles = {
  marginRight: '8px',
};

interface IconMarkValidationSettingsProps {
  trainingSetId: string;
  isValidationLoading?: boolean;
}

export default function IconMarkValidationSettings({
  trainingSetId,
  isValidationLoading = false,
}: IconMarkValidationSettingsProps) {
  const { accessibility, setAccessibility } = React.useContext(AppContext);
  const validationsEnabled = useAtomValue(markValidationsEnabledAtom);
  const [showValidations, setShowValidations] = useAtom(markShowValidationsAtom);
  const [, setShowValidationErrorsDialog] = useAtom(showMarkValidationErrorsDialogAtom);
  const validationByDocument = useAtomValue(markValidationByDocumentAtom);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();

  const handleClick = () => {
    const el = document.getElementById('icon-mark-validation-settings');
    if (el) setAnchorEl(el);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleValidationsClick = () => {
    setShowValidations(!showValidations);
  };

  const calculateHasErrors = () => {
    const totalNonDismissedErrors = validationByDocument.reduce((total, doc) => {
      const nonDismissedErrors =
        doc.validationErrorDetails?.filter((error) => !error.dismissalReason) || [];
      return total + nonDismissedErrors.length;
    }, 0);
    return totalNonDismissedErrors > 0;
  };

  const hasErrors = calculateHasErrors();

  const hasValidationData =
    validationByDocument.length > 0 &&
    validationByDocument.some((doc) => doc.hasValidationResponse);

  // Get badge color based on validation state
  const getBadgeColor = () => {
    if (!validationsEnabled) {
      return 'default';
    }
    // If loading or no validation data yet, show warning
    if (!hasValidationData || isValidationLoading) {
      return 'warning'; // Missing/loading validation data
    }
    if (hasErrors) {
      return 'error'; // Has validation errors
    }
    return 'success'; // No errors
  };

  const handleToggleAccessibilityClick = () => {
    setAccessibility(!accessibility);
  };

  const handleRevalidateClick = () => {
    queryClient.invalidateQueries({
      queryKey: ['mark-validation', trainingSetId]
    });
    setShowValidations(true);
    handleClose();
  };

  const handleViewValidationErrorsClick = () => {
    setShowValidationErrorsDialog(true);
    handleClose();
  };

  return (
    <div>
      <IconButtonWhiteBg
        disabled={false}
        onClick={handleClick}
        id="icon-mark-validation-settings"
        cypressId="btn-mark-validation-settings"
        tooltip="Validation Settings"
        sx={{ '& svg path': { fill: 'black', stroke: 'black' } }}
      >
        {validationsEnabled && showValidations ? (
          <Badge variant="dot" color={getBadgeColor()}>
            {isValidationLoading ? (
              <CircularProgress size={14} sx={{ color: 'black' }} />
            ) : (
              <SvgIconValidate style={{ color: 'black', width: '14px', height: '14px' }} />
            )}
          </Badge>
        ) : (
          <>
            {isValidationLoading ? (
              <CircularProgress size={14} sx={{ color: 'black' }} />
            ) : (
              <SvgIconValidate style={{ color: 'black', width: '14px', height: '14px' }} />
            )}
          </>
        )}
      </IconButtonWhiteBg>
      <Menu
        id="mark-validation-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'mark-validation-button',
        }}
      >
        <MenuItem
          onClick={handleRevalidateClick}
          disabled={!validationsEnabled}
          data-cy="menu-mark-validation-revalidate"
        >
          <RefreshIcon sx={iconStyles} />
          <span style={textStyles}>Revalidate</span>
        </MenuItem>

        <MenuItem
          onClick={handleViewValidationErrorsClick}
          disabled={!validationsEnabled}
          data-cy="menu-mark-validation-view-errors"
        >
          <ListAltIcon sx={iconStyles} />
          <span style={textStyles}>View Validation Errors</span>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleToggleValidationsClick}
          disabled={!validationsEnabled}
          data-cy="menu-mark-validation-show-validations"
        >
          <RemoveRedEyeIcon sx={iconStyles} />
          <span style={textStyles}>Show Validations</span>
          {showValidations ? (
            <CheckIcon sx={{ ...statusIconStyles, color: 'green' }} />
          ) : (
            <CloseIcon sx={{ ...statusIconStyles, color: 'red' }} />
          )}
        </MenuItem>

        <MenuItem
          onClick={handleToggleAccessibilityClick}
          disabled={!validationsEnabled}
          data-cy="menu-mark-validation-enhanced-contrast"
        >
          <ContrastIcon sx={iconStyles} />
          <span style={textStyles}>Enhanced Contrast</span>
          {accessibility ? (
            <CheckIcon sx={{ ...statusIconStyles, color: 'green' }} />
          ) : (
            <CloseIcon sx={{ ...statusIconStyles, color: 'red' }} />
          )}
        </MenuItem>
      </Menu>
    </div>
  );
}
